import * as React from "react";
import {useState} from "react";
import {useParams} from "react-router-dom";
import {Box, Chip, Typography} from "@mui/material";
import {
   Timeline,
   TimelineConnector,
   TimelineContent,
   TimelineDot,
   TimelineItem,
   TimelineOppositeContent,
   TimelineSeparator
} from "@mui/lab";
import dayjs from "dayjs";
import Page from "../../../layout/Page";
import PageHeader from "../../../layout/PageHeader";
import PageContent from "../../../layout/PageContent";
import {BodyText} from "../../../layout/Typography";
import {ParamBrandHouse} from "../../../Parameters";
import {useAuditNoteDetailsQuery, useAuditNoteDetailsUserQuery} from "../../../Queries";

const getTypeColor = (type: string): string => {
   const colors: Record<string, string> = {
      'BrandSetting': '#1976d2',
      'BrandRule': '#9c27b0',
      'BrandLocalization': '#2e7d32',
      'BrickMail': '#ed6c02',
   };
   return colors[type] || '#757575';
};

export const AuditNoteDetailsPage = () => {
   const {auditNoteId} = useParams<ParamBrandHouse & {auditNoteId: string}>();

   const {data, loading} = useAuditNoteDetailsQuery({
      variables: {
         auditNoteId: auditNoteId!
      },
      fetchPolicy: "no-cache"
   });

   const auditNote = data?.AuditNote;
   const patches = data?.PatchMany || [];

   const pageTitle = auditNote?.createdAt
      ? dayjs(auditNote.createdAt).format("D MMMM YYYY, HH:mm")
      : "Audit Change Details";

   return (
      <Page>
         <PageHeader title={pageTitle} onBack={true} />
         <PageContent>
            {loading && <Typography>Loading...</Typography>}
            {!loading && auditNote && (
               <Box sx={{marginBottom: 3}}>
                  <Typography variant="body1" color="textSecondary">
                     {auditNote.explanation}
                  </Typography>
               </Box>
            )}
            {!loading && patches.length === 0 && (
               <Typography color="textSecondary">
                  No changes recorded for this audit change.
               </Typography>
            )}
            {!loading && patches.length > 0 && (
               <Box sx={{width: '100%'}}>
                  <Timeline position="right">
                     {patches.map((patch: any) => (
                        <TimelineItem key={patch._id}>
                           <TimelineOppositeContent
                              sx={{flex: 0.25, paddingRight: "16px", textAlign: "right"}}
                           >
                              <Typography variant="body2" color="textSecondary">
                                 {dayjs(patch.date).format("D MMM YYYY, HH:mm")}
                              </Typography>
                              <Chip
                                 label={patch.type}
                                 size="small"
                                 sx={{
                                    mt: 0.5,
                                    backgroundColor: getTypeColor(patch.type),
                                    color: '#fff',
                                    fontWeight: 500
                                 }}
                              />
                           </TimelineOppositeContent>

                           <TimelineSeparator>
                              <TimelineDot />
                              <TimelineConnector />
                           </TimelineSeparator>

                           <TimelineContent sx={{flex: 1, paddingLeft: "16px", maxWidth: "calc(100% - 150px)"}}>
                              <Box>
                                 <BodyText type={"body1"} sx={{fontWeight: 'bold'}}>
                                    {patch.updatedBy?.ref && <HistoryUser userId={patch.updatedBy.ref} />}
                                    {!patch.updatedBy?.ref && <div>API/Brand</div>}
                                 </BodyText>
                                 <ul>
                                    {patch.ops?.map((op: any, index: number) => (
                                       <li key={index}>
                                          <Typography variant="body2">
                                             <PatchDescription {...op} />
                                          </Typography>
                                       </li>
                                    ))}
                                 </ul>
                              </Box>
                           </TimelineContent>
                        </TimelineItem>
                     ))}
                  </Timeline>
               </Box>
            )}
         </PageContent>
      </Page>
   );
};

const HistoryUser = ({userId}: {userId: string}) => {
   const {data} = useAuditNoteDetailsUserQuery({
      variables: {
         id: userId
      }
   });
   return <div>{data?.User?.name || data?.User?.email || 'API / Brand'}</div>;
};

type PatchDescriptionProps = {
   value?: any | null;
   op?: string | null;
   path?: string | null;
};

const PatchDescription = ({value, path, op}: PatchDescriptionProps) => {
   const [expanded, setExpanded] = useState(false);
   const formattedPath = Array.isArray(path) ? path.join('.') : path;
   const valueString = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
   const isLargeValue = valueString.length > 100;

   const toggleExpanded = () => {
      setExpanded(!expanded);
   };

   const renderPath = () => (
      <Typography
         component="span"
         sx={{
            color: 'red',
            backgroundColor: 'lightyellow',
            padding: '2px 4px',
            borderRadius: '4px',
            fontFamily: 'monospace'
         }}
      >
         {formattedPath}
      </Typography>
   );

   const renderValue = () => {
      if (!isLargeValue) {
         return (
            <Box sx={{
               fontFamily: 'monospace',
               maxWidth: '100%',
               overflowX: 'auto',
               whiteSpace: 'pre-wrap',
               wordBreak: 'break-word',
               backgroundColor: 'rgba(0,0,0,0.04)',
               padding: '8px',
               borderRadius: '4px'
            }}>
               {valueString}
            </Box>
         );
      }

      return (
         <Box>
            {expanded ? (
               <Box sx={{
                  fontFamily: 'monospace',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  padding: '8px',
                  borderRadius: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto'
               }}>
                  {valueString}
               </Box>
            ) : (
               <Box sx={{
                  fontFamily: 'monospace',
                  maxWidth: '100%',
                  overflowX: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  padding: '8px',
                  borderRadius: '4px',
                  cursor: 'pointer'
               }} onClick={toggleExpanded}>
                  {valueString.substring(0, 100)}... <Typography component="span" color="primary" sx={{fontSize: '0.9em'}}>(Click to expand)</Typography>
               </Box>
            )}
            {expanded && (
               <Typography variant="body2" color="primary" sx={{cursor: 'pointer', mt: 1}} onClick={toggleExpanded}>
                  Collapse
               </Typography>
            )}
         </Box>
      );
   };

   switch (op) {
      case 'add':
      case 'replace':
         return (
            <>
               <Typography variant="body2">
                  Updated {renderPath()} to:
               </Typography>
               <Box sx={{paddingLeft: 2, width: '100%'}}>
                  {renderValue()}
               </Box>
            </>
         );
      case 'remove':
         return (
            <Typography variant="body2">
               Removed {renderPath()}
            </Typography>
         );
      default:
         return (
            <Typography variant="body2">
               Unknown operation {op}
            </Typography>
         );
   }
};
